import React, { useState, useEffect } from 'react';
import { Modal, Table, Typography, Spin, Alert, Tag } from 'antd';
import { HeartOutlined, DashboardOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;

// Helper function to get display name for vital parameter
const getVitalDisplayName = (parameterName) => {
  const vitalDisplayNames = {
    'bloodPressureSystolic': 'Systolic Blood Pressure',
    'bloodPressureDiastolic': 'Diastolic Blood Pressure',
    'heartRate': 'Heart Rate',
    'respiratoryRate': 'Respiratory Rate',
    'temperature': 'Temperature',
    'oxygenSaturation': 'Oxygen Saturation',
    'weight': 'Weight',
    'height': 'Height',
    'bmi': 'BMI',
    'painScale': 'Pain Scale'
  };
  
  return vitalDisplayNames[parameterName] || parameterName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

// Helper function to get vital units
const getVitalUnits = (parameterName) => {
  const vitalUnits = {
    'bloodPressureSystolic': 'mmHg',
    'bloodPressureDiastolic': 'mmHg',
    'heartRate': 'bpm',
    'respiratoryRate': '/min',
    'temperature': '°C',
    'oxygenSaturation': '%',
    'weight': 'lbs',
    'height': 'in',
    'bmi': 'kg/m²',
    'painScale': '/10'
  };

  return vitalUnits[parameterName] || '';
};

// Helper function to get vital icon
const getVitalIcon = (parameterName) => {
  if (parameterName.includes('heartRate')) return <HeartOutlined />;
  if (parameterName.includes('temperature')) return <DashboardOutlined />;
  return <DashboardOutlined />;
};

// Helper function to determine if vital is abnormal
const isVitalAbnormal = (parameterName, value) => {
  if (!value || isNaN(value)) return false;

  let numValue = parseFloat(value);

  // Convert temperature to Celsius for comparison if it appears to be in Fahrenheit
  if (parameterName === 'temperature' && numValue > 50) {
    numValue = (numValue - 32) * 5/9;
  }

  const normalRanges = {
    'bloodPressureSystolic': { min: 90, max: 140 },
    'bloodPressureDiastolic': { min: 60, max: 90 },
    'heartRate': { min: 60, max: 100 },
    'respiratoryRate': { min: 12, max: 20 },
    'temperature': { min: 36.1, max: 37.5 }, // Normal range in Celsius (97.0-99.5°F)
    'oxygenSaturation': { min: 95, max: 100 }
  };

  const range = normalRanges[parameterName];
  if (!range) return false;

  return numValue < range.min || numValue > range.max;
};

// Helper function to get vital flag
const getVitalFlag = (parameterName, value) => {
  if (!isVitalAbnormal(parameterName, value)) return null;

  let numValue = parseFloat(value);

  // Convert temperature to Celsius for comparison if it appears to be in Fahrenheit
  if (parameterName === 'temperature' && numValue > 50) {
    numValue = (numValue - 32) * 5/9;
  }

  const normalRanges = {
    'bloodPressureSystolic': { min: 90, max: 140 },
    'bloodPressureDiastolic': { min: 60, max: 90 },
    'heartRate': { min: 60, max: 100 },
    'respiratoryRate': { min: 12, max: 20 },
    'temperature': { min: 36.1, max: 37.5 }, // Normal range in Celsius (97.0-99.5°F)
    'oxygenSaturation': { min: 95, max: 100 }
  };

  const range = normalRanges[parameterName];
  if (!range) return null;

  if (numValue < range.min) return 'L';
  if (numValue > range.max) return 'H';
  return null;
};

// Helper function to get flag color
const getFlagColor = (flag) => {
  if (!flag) return 'default';
  
  switch (flag.toUpperCase()) {
    case 'H':
    case 'HIGH':
      return 'orange';
    case 'L':
    case 'LOW':
      return 'blue';
    case 'C':
    case 'CRITICAL':
      return 'red';
    default:
      return 'default';
  }
};

const VitalsHistoryModal = ({
  visible,
  onClose,
  parameterName,
  vitalsEntries = [],
  loading = false
}) => {
  const [historyData, setHistoryData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    if (visible && parameterName && vitalsEntries.length > 0) {
      setDataLoading(true);
      try {
        // Filter and process vitals history for the selected parameter
        const history = vitalsEntries
          .filter(entry => {
            // Check if this entry has the vital parameter
            return entry.vitals && entry.vitals[parameterName] !== undefined && entry.vitals[parameterName] !== null;
          })
          .map(entry => {
            let displayValue = entry.vitals[parameterName];

            // Convert temperature to Celsius if it appears to be in Fahrenheit
            if (parameterName === 'temperature') {
              const numValue = parseFloat(displayValue);
              if (!isNaN(numValue)) {
                // If temperature is > 50, assume it's in Fahrenheit and convert to Celsius
                if (numValue > 50) {
                  displayValue = ((numValue - 32) * 5/9).toFixed(1);
                } else {
                  // Already in Celsius, just format to 1 decimal place
                  displayValue = numValue.toFixed(1);
                }
              }
            }

            return {
              key: `${entry._id || entry.id}-${parameterName}`,
              entryDate: entry.entryDate || entry.date,
              value: displayValue,
              units: getVitalUnits(parameterName),
              flag: getVitalFlag(parameterName, entry.vitals[parameterName]),
              source: entry.source || 'Vitals',
              isAbnormal: isVitalAbnormal(parameterName, entry.vitals[parameterName])
            };
          })
          .sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate)); // Most recent first

        setHistoryData(history);
      } catch (error) {
        console.error('Error processing vitals history:', error);
        setHistoryData([]);
      } finally {
        setDataLoading(false);
      }
    }
  }, [visible, parameterName, vitalsEntries]);

  // Safety check for required props - after all hooks
  if (!parameterName) {
    return null;
  }

  const displayName = getVitalDisplayName(parameterName);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'entryDate',
      key: 'entryDate',
      width: 120,
      render: (date) => moment(date).format('MM/DD/YYYY'),
      sorter: (a, b) => new Date(b.entryDate) - new Date(a.entryDate),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Time',
      dataIndex: 'entryDate',
      key: 'time',
      width: 100,
      render: (date) => moment(date).format('HH:mm'),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value, record) => (
        <span style={{ 
          color: record.isAbnormal ? '#d4380d' : '#000',
          fontWeight: record.isAbnormal ? 'bold' : 'normal'
        }}>
          {value} {record.units}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'flag',
      key: 'flag',
      width: 80,
      render: (flag) => {
        if (!flag) return <Tag color="green">Normal</Tag>;
        
        const flagText = flag === 'H' ? 'HIGH' : flag === 'L' ? 'LOW' : flag;
        return <Tag color={getFlagColor(flag)}>{flagText}</Tag>;
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 100,
    }
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {getVitalIcon(parameterName)}
          <span style={{ marginLeft: 8 }}>
            {displayName} History
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
    >
      {dataLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading vitals history...</Text>
          </div>
        </div>
      ) : historyData.length === 0 ? (
        <Alert
          message="No History Available"
          description={`No historical data found for ${displayName}.`}
          type="info"
          showIcon
        />
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Showing {historyData.length} historical entries for {displayName}
            </Text>
          </div>
          
          <Table
            columns={columns}
            dataSource={historyData}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} entries`
            }}
            scroll={{ y: 400 }}
            size="small"
          />
        </>
      )}
    </Modal>
  );
};

export default VitalsHistoryModal;
